import React from 'react';
import {useMatch, useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {ECurrencySymbol} from '@edna/utils';

import {useGetLeadClientQuery} from 'src/containers/LeadCompanyClients/api';
import {ELicensePeriod} from 'src/containers/Licenses/definitions';
import {ELicenseLabelType, ETariffDetailType} from 'src/containers/TariffsCatalog';
import PATHS from 'src/paths';

import {
  useGetCatalogTariffDetailsQuery,
  useGetCountriesPricingQuery,
  useGetLicensePricingQuery,
} from '../../api';
import {TAvailableTariff, TTariffDetailFinalPrice} from '../../definitions';
import {useLicenseInfo} from '../../useLicenseInfo';
import {formatPrice, getFinalPrice} from '../../utils';

type TLicensePrice = {
  period: ELicensePeriod;
  currency?: ECurrencySymbol | null;
  licensePrice: string | number;
  connectionFee: string | number;
  amauPrice: string | number;
  mauCount?: string | number | null;
  packagePrice?: string | null;
};

const useLicensePrices = (
  tariff: TAvailableTariff,
  usePublicPrices?: boolean,
): TLicensePrice | null => {
  const {id} = useParams();
  const isPersonalPrices = !!useMatch({path: PATHS.COMPANY_PROFILE, end: false}) || usePublicPrices;
  const isSupportView = !!useMatch({path: PATHS.SUPPORT_CLIENTS, end: false});
  const hasNoTariffIdYet = !!useMatch({path: PATHS.COMPANY_CLIENT_EDIT, end: false});

  const {data: leadClient} = useGetLeadClientQuery(id ?? skipToken, {
    skip: isSupportView || isPersonalPrices,
  });
  const {data: licenseInfo} = useGetLicensePricingQuery(
    {
      availableTariffId: tariff.id,
      tenantId: isSupportView ? id : leadClient?.reservedTenantId,
    },
    {skip: hasNoTariffIdYet || (isSupportView ? false : !leadClient && !isPersonalPrices)},
  );

  const licensePrices = React.useMemo(() => {
    if (hasNoTariffIdYet || !licenseInfo) {
      return null;
    }

    const prices = licenseInfo.prices.reduce<Record<string, number>>(
      (result, price) => ({
        ...result,
        [price.label]: price.price,
      }),
      {},
    );

    return {
      period: licenseInfo.data?.periodMonths === 12 ? ELicensePeriod.YEAR : ELicensePeriod.MONTH,
      currency: ECurrencySymbol[licenseInfo.currency],
      licensePrice: formatPrice(prices[ELicenseLabelType.LICENSE]),
      // WhatsApp
      connectionFee: formatPrice(prices[ELicenseLabelType.CONNECTION_FEE]),
      amauPrice: formatPrice(prices[ELicenseLabelType.AMAU]),
      mauCount: licenseInfo.data?.mauCount ? formatPrice(licenseInfo.data?.mauCount) : null,
      // Viber
      packagePrice: formatPrice(prices[ELicenseLabelType.PACKAGE]),
    };
  }, [tariff, licenseInfo, hasNoTariffIdYet]);

  const catalogLicenseInfo = useLicenseInfo({
    tariffCatalogId: tariff.tariffCatalogId,
    licenseDetailId: tariff.licenseDetailId,
    skip: !hasNoTariffIdYet || isPersonalPrices,
  });

  const catalogLicensePrices = React.useMemo(() => {
    if (!hasNoTariffIdYet || !catalogLicenseInfo) {
      return null;
    }

    return {
      period: catalogLicenseInfo.period,
      currency: catalogLicenseInfo.currency,
      licensePrice: getFinalPrice(
        catalogLicenseInfo.licensePrice,
        tariff.data[ELicenseLabelType.LICENSE],
        true,
      ),
      // WhatsApp
      connectionFee: getFinalPrice(
        catalogLicenseInfo.connectionFee,
        tariff.data[ELicenseLabelType.CONNECTION_FEE],
        true,
      ),
      amauPrice: getFinalPrice(
        catalogLicenseInfo.amauPrice,
        tariff.data[ELicenseLabelType.AMAU],
        true,
      ),
      mauCount: catalogLicenseInfo.mauCount ? formatPrice(catalogLicenseInfo.mauCount) : null,
      // Viber
      packagePrice:
        typeof catalogLicenseInfo.packagePrice === 'number'
          ? formatPrice(catalogLicenseInfo.packagePrice)
          : null,
    } as TLicensePrice;
  }, [hasNoTariffIdYet, tariff, catalogLicenseInfo]);

  return hasNoTariffIdYet ? catalogLicensePrices : licensePrices;
};

type TCountryPrice = Omit<TTariffDetailFinalPrice, 'prices'> & {
  prices: Record<string, string | number>;
};

const useCountriesPrices = (
  tariff: TAvailableTariff,
): {currency: ECurrencySymbol | null; prices: TCountryPrice[]} | null => {
  const {id: supportTenantId} = useParams<'id'>();

  const {data: catalogTariffDetails = [], fulfilledTimeStamp: catalogTimeStamp} =
    useGetCatalogTariffDetailsQuery(
      {
        id: tariff.tariffCatalogId,
        detailTypes: [ETariffDetailType.REGION],
      },
      {skip: !supportTenantId},
    );

  const supportCountriesPrices = React.useMemo(() => {
    if (!supportTenantId || !catalogTariffDetails.length) {
      return null;
    }

    const prices = catalogTariffDetails.map((detail) => ({
      currency: detail.currency,
      country: detail.country,
      data: detail.data,
      prices: detail.pricingDetails.reduce<TCountryPrice['prices']>(
        (result, pricingDetail) => ({
          ...result,
          [pricingDetail.label]: formatPrice(pricingDetail.data?.price ?? 0, 4),
        }),
        {},
      ),
    }));

    return {currency: prices[0] ? ECurrencySymbol[prices[0].currency] : null, prices};
  }, [supportTenantId, catalogTimeStamp]);

  const {data: userCountriesInfo = []} = useGetCountriesPricingQuery(tariff.id, {
    skip: !!supportTenantId,
  });

  const userCountriesPrices = React.useMemo(() => {
    if (supportTenantId) {
      return null;
    }

    const prices = userCountriesInfo.map((countryInfo) => ({
      currency: countryInfo.currency,
      country: countryInfo.country,
      data: countryInfo.data,
      prices: countryInfo.prices.reduce<TCountryPrice['prices']>(
        (result, pricingDetail) => ({
          ...result,
          [pricingDetail.label]: formatPrice(pricingDetail.price, 4),
        }),
        {},
      ),
    }));

    return {currency: prices[0] ? ECurrencySymbol[prices[0].currency] : null, prices};
  }, [supportTenantId, userCountriesInfo]);

  return supportTenantId ? supportCountriesPrices : userCountriesPrices;
};

export {useLicensePrices, useCountriesPrices};
